import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const FacialScanContext = createContext();

const SCAN_STATE = {
  IDLE: 'idle',
  STARTING: 'starting',
  COUNTDOWN: 'countdown',
  SCANNING: 'scanning',
  PROCESSING: 'processing',
  COMPLETE: 'complete',
  ERROR: 'error'
};

export function FacialScanProvider({ children }) {
  const [scanState, setScanState] = useState(SCAN_STATE.IDLE);
  const [progress, setProgress] = useState(0);
  const [countdown, setCountdown] = useState(3);
  const [scanDuration] = useState(10); // 10 seconds scan
  const [retryCount, setRetryCount] = useState(0);
  const [errorReason, setErrorReason] = useState(null);
  const [scanResults, setScanResults] = useState(null);
  const [waveformData, setWaveformData] = useState([]);

  // Generate mock waveform data during scan
  useEffect(() => {
    if (scanState === SCAN_STATE.SCANNING) {
      const interval = setInterval(() => {
        setWaveformData(prev => {
          const newPoint = 50 + Math.sin(Date.now() / 200) * 30 + (Math.random() - 0.5) * 10;
          const updated = [...prev, newPoint].slice(-50);
          return updated;
        });
      }, 100);
      return () => clearInterval(interval);
    }
  }, [scanState]);

  const startScan = useCallback(() => {
    setScanState(SCAN_STATE.STARTING);
    setProgress(0);
    setErrorReason(null);
    setScanResults(null);
    setWaveformData([]);
  }, []);

  const beginCountdown = useCallback(() => {
    setScanState(SCAN_STATE.COUNTDOWN);
    setCountdown(3);
  }, []);

  const beginScanning = useCallback(() => {
    setScanState(SCAN_STATE.SCANNING);
    setProgress(0);
  }, []);

  const updateProgress = useCallback((value) => {
    setProgress(value);
  }, []);

  const completeScan = useCallback((results) => {
    setScanState(SCAN_STATE.COMPLETE);
    setScanResults(results);
  }, []);

  const failScan = useCallback((reason) => {
    setScanState(SCAN_STATE.ERROR);
    setErrorReason(reason);
    setRetryCount(prev => prev + 1);
  }, []);

  const retryScan = useCallback(() => {
    if (retryCount < 2) {
      startScan();
    }
  }, [retryCount, startScan]);

  const resetScan = useCallback(() => {
    setScanState(SCAN_STATE.IDLE);
    setProgress(0);
    setCountdown(3);
    setRetryCount(0);
    setErrorReason(null);
    setScanResults(null);
    setWaveformData([]);
  }, []);

  const generateMockResults = useCallback(() => {
    // Simulate 80% success rate
    const isSuccess = Math.random() > 0.2;
    
    if (!isSuccess) {
      const reasons = [
        'Poor lighting detected',
        'Too much movement',
        'Face not clearly visible',
        'Signal quality too low'
      ];
      return { success: false, reason: reasons[Math.floor(Math.random() * reasons.length)] };
    }

    return {
      success: true,
      scan_type: 'facial_rPPG',
      metrics: {
        heart_rate: { value: 60 + Math.floor(Math.random() * 30), confidence: 0.85 + Math.random() * 0.1 },
        breathing_rate: { value: 12 + Math.floor(Math.random() * 8), confidence: 0.88 + Math.random() * 0.1 },
        hrv_sdnn: { value: 30 + Math.floor(Math.random() * 30), confidence: 0.80 + Math.random() * 0.15 },
        stress_index: { value: 20 + Math.floor(Math.random() * 60), confidence: 0.75 + Math.random() * 0.15 },
        spo2_estimate: { value: 95 + Math.floor(Math.random() * 4), confidence: 0.70 + Math.random() * 0.15 },
        facial_skin_age: { value: 25 + Math.floor(Math.random() * 20), confidence: 0.72 + Math.random() * 0.15 }
      },
      scan_confidence: Math.random() > 0.5 ? 'high' : 'medium',
      timestamp: new Date().toISOString()
    };
  }, []);

  return (
    <FacialScanContext.Provider value={{
      scanState,
      setScanState,
      progress,
      countdown,
      setCountdown,
      scanDuration,
      retryCount,
      errorReason,
      scanResults,
      waveformData,
      startScan,
      beginCountdown,
      beginScanning,
      updateProgress,
      completeScan,
      failScan,
      retryScan,
      resetScan,
      generateMockResults,
      SCAN_STATE
    }}>
      {children}
    </FacialScanContext.Provider>
  );
}

export const useFacialScan = () => {
  const context = useContext(FacialScanContext);
  if (!context) {
    throw new Error('useFacialScan must be used within a FacialScanProvider');
  }
  return context;
};

export { SCAN_STATE };
